/**
 * ScarDetail (C-8) — single SCAR: metadata, lifecycle FSM actions, supplier
 * response panel (root cause + corrective/preventive action), Related Records
 * (C-4), history. Route: /scars/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from '../Recalls/Recalls.module.css';
import { RelatedRecords } from '../../components/RelatedRecords';
import {
  useScarDetailQuery, useScarTransition, useScarRespond,
  SCAR_FSM, SCAR_STATUS_LABEL, SCAR_SOURCE_LABEL, SCAR_SEVERITY_LABEL,
} from './useScar';

function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function sevClass(s: string) {
  return s === 'CRITICAL' ? styles.cls1 : s === 'MAJOR' ? styles.cls2 : styles.cls3;
}

export function ScarDetail({ scarId }: { scarId: string }) {
  const q = useScarDetailQuery(scarId);
  const transition = useScarTransition(scarId);
  const respond = useScarRespond(scarId);
  const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [resp, setResp] = useState({ rootCause: '', correctiveAction: '', preventiveAction: '', supplierResponse: '' });

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading SCAR…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>SCAR not found. <Link to="/scars">Back to SCARs</Link></div></main>;
  const s = q.data;
  const next = SCAR_FSM[s.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try { await transition.mutateAsync({ toStatus: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Transition failed.'); }
  }
  function openResponse() {
    setResp({
      rootCause: s.rootCause ?? '', correctiveAction: s.correctiveAction ?? '',
      preventiveAction: s.preventiveAction ?? '', supplierResponse: s.supplierResponse ?? '',
    });
    setEditing(true);
  }
  async function saveResponse() {
    setMsg(null);
    try { await respond.mutateAsync(resp); setEditing(false); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not save the response.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/scars" className={styles.crumb}>SCARs</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{s.scarNumber}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{s.scarNumber}</span> · {s.title}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{SCAR_STATUS_LABEL[s.status] ?? s.status}</span>
            <span className={`${styles.pill} ${sevClass(s.severity)}`}>{SCAR_SEVERITY_LABEL[s.severity] ?? s.severity}</span>
            <span className={styles.pill}>{SCAR_SOURCE_LABEL[s.source] ?? s.source}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {next.length === 0 ? <span className={styles.terminal}>Terminal state</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${(to === 'CANCELLED' || to === 'REJECTED') ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>→ {SCAR_STATUS_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>SCAR detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Supplier</dt><dd>{s.supplierName ?? '—'}</dd></div>
          <div><dt>Source</dt><dd>{SCAR_SOURCE_LABEL[s.source] ?? s.source}</dd></div>
          <div><dt>Severity</dt><dd>{SCAR_SEVERITY_LABEL[s.severity] ?? s.severity}</dd></div>
          <div><dt>Owner</dt><dd>{s.ownerName ?? '—'}</dd></div>
          <div><dt>Issued</dt><dd>{fmtDate(s.issuedDate)}</dd></div>
          <div><dt>Response due</dt><dd>{fmtDate(s.responseDueDate)}</dd></div>
          <div><dt>Response received</dt><dd>{fmtDate(s.responseReceivedDate)}</dd></div>
          <div><dt>Closed</dt><dd>{fmtDT(s.closedAt)}</dd></div>
        </dl>
        {s.description && <div className={styles.descBlock}><strong>Description</strong><p>{s.description}</p></div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Supplier response &amp; corrective action</h2>
        {!editing ? (
          <>
            <dl className={styles.metaGrid}>
              <div><dt>Root cause</dt><dd>{s.rootCause ?? '—'}</dd></div>
              <div><dt>Corrective action</dt><dd>{s.correctiveAction ?? '—'}</dd></div>
              <div><dt>Preventive action</dt><dd>{s.preventiveAction ?? '—'}</dd></div>
              <div><dt>Supplier response</dt><dd>{s.supplierResponse ?? '—'}</dd></div>
            </dl>
            {s.status !== 'CLOSED' && s.status !== 'CANCELLED' && (
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={openResponse}>
                {s.rootCause ? 'Edit response' : 'Record supplier response'}
              </button>
            )}
          </>
        ) : (
          <div>
            <label className={styles.field}><span>Root cause</span>
              <textarea rows={2} value={resp.rootCause} onChange={(e) => setResp({ ...resp, rootCause: e.target.value })} /></label>
            <label className={styles.field}><span>Corrective action</span>
              <textarea rows={2} value={resp.correctiveAction} onChange={(e) => setResp({ ...resp, correctiveAction: e.target.value })} /></label>
            <label className={styles.field}><span>Preventive action</span>
              <textarea rows={2} value={resp.preventiveAction} onChange={(e) => setResp({ ...resp, preventiveAction: e.target.value })} /></label>
            <label className={styles.field}><span>Supplier response / notes</span>
              <textarea rows={2} value={resp.supplierResponse} onChange={(e) => setResp({ ...resp, supplierResponse: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setEditing(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void saveResponse()} disabled={respond.isPending}>
                {respond.isPending ? 'Saving…' : 'Save response'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* C-4 — Related Records (Deviation / Complaint / Audit finding / CAPA / …). */}
      <RelatedRecords entityType="SCAR" entityId={s.scarId} />

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(s.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.event_kind === 'SCAR_TRANSITION' && h.from_status
                  ? `${SCAR_STATUS_LABEL[h.from_status] ?? h.from_status} → ${SCAR_STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}`
                  : h.event_kind.replaceAll('_', ' ').toLowerCase()}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
